// What the jump bar's sources read in the app: GitHub through the app's
// GraphQL client (api/gh.ts, with its scheduling, dedupe and retries), and
// the dashboard as last read (state/dashCache.ts). The sources themselves
// are jump/sources.ts.
import { graphql } from '../api/gh';
import { RepoTree } from '../state/repotree.svelte';
import { dashboardStorage } from '../state/dashCache';
import type { Source } from './complete';
import type { InterpretContext } from './interpret';
import type { Place, Repo } from './rows';
import { branchSource, dashboardOf, dashboardSource, directSource, pathSource, pinSource, pullSource, recentSource, repositorySource, waitingOf, waitingSource, type Branches, type OpenPull, type RefTree, type WaitingPull } from './sources';

/** How many branches one prefix lists. */
export const BRANCHES_LISTED = 20;

/**
 * A repository's branches whose names match `$query` (GitHub's filter on
 * the name), the most recently committed to first, each with its open
 * pull request; and the default branch, which a branch is reviewed against.
 */
export const JUMP_REFS = `
query JumpRefs($owner: String!, $repo: String!, $query: String!) {
  repository(owner: $owner, name: $repo) {
    defaultBranchRef { name }
    refs(refPrefix: "refs/heads/", query: $query, first: ${BRANCHES_LISTED}, orderBy: { field: TAG_COMMIT_DATE, direction: DESC }) {
      nodes { name associatedPullRequests(states: OPEN, first: 1, orderBy: { field: UPDATED_AT, direction: DESC }) { nodes { number title } } }
    }
  }
}`;

interface RefsAnswer {
  repository: {
    defaultBranchRef: { name: string } | null;
    refs: { nodes: ({ name: string; associatedPullRequests: { nodes: ({ number: number; title: string } | null)[] } } | null)[] } | null;
  } | null;
}

/** The branches of `repo` matching `prefix`; null when GitHub shows no such repository. */
export async function readBranches(repo: Repo, prefix: string, signal: AbortSignal): Promise<Branches | null> {
  const r = await graphql<RefsAnswer>(JUMP_REFS, { owner: repo.owner, repo: repo.repo, query: prefix }, { priority: 'interactive', signal });
  if (!r.repository) return null;
  return {
    defaultBranch: r.repository.defaultBranchRef?.name ?? null,
    branches: (r.repository.refs?.nodes ?? []).flatMap((n) => (n ? [{ name: n.name, pull: n.associatedPullRequests.nodes.find((p) => !!p) ?? null }] : [])),
  };
}

/** How many open pull requests one repository lists for completion. */
export const PULLS_LISTED = 100;

/** A repository's open pull requests, the most recently updated first: what a title match needs, and nothing more (no checks, no counts). */
export const JUMP_PULLS = `
query JumpPulls($owner: String!, $repo: String!) {
  repository(owner: $owner, name: $repo) {
    pullRequests(first: ${PULLS_LISTED}, states: OPEN, orderBy: { field: UPDATED_AT, direction: DESC }) { nodes { number title author { login } } }
  }
}`;

/** The repository's open pull requests; null when GitHub shows no such repository. */
export async function readOpenPulls(repo: Repo): Promise<OpenPull[] | null> {
  const r = await graphql<{ repository: { pullRequests: { nodes: ({ number: number; title: string; author: { login: string } | null } | null)[] } } | null }>(JUMP_PULLS, { owner: repo.owner, repo: repo.repo }, { priority: 'interactive' });
  return r.repository?.pullRequests.nodes.flatMap((p) => (p ? [{ number: p.number, title: p.title, author: p.author?.login ?? null }] : [])) ?? null;
}

/** Whether GitHub shows a repository, as GitHub spells its name. */
export const JUMP_REPO = `
query JumpRepo($owner: String!, $repo: String!) {
  repository(owner: $owner, name: $repo) { name owner { login } }
}`;

export async function readRepository(repo: Repo, signal: AbortSignal): Promise<Repo | null> {
  const r = await graphql<{ repository: { name: string; owner: { login: string } } | null }>(JUMP_REPO, { owner: repo.owner, repo: repo.repo }, { priority: 'interactive', signal });
  return r.repository ? { owner: r.repository.owner.login, repo: r.repository.name } : null;
}

/** The commit a ref names (a tag's through its tag object). */
export const JUMP_TREE = `
query JumpTree($owner: String!, $repo: String!, $ref: String!) {
  repository(owner: $owner, name: $repo) {
    object(expression: $ref) {
      ... on Commit { oid }
      ... on Tag { target { ... on Commit { oid } } }
    }
  }
}`;

/** The commit `ref` names in `repo`; null when it names none. */
export async function readCommit(repo: Repo, ref: string, signal: AbortSignal): Promise<string | null> {
  const r = await graphql<{ repository: { object: { oid?: string; target?: { oid?: string } } | null } | null }>(JUMP_TREE, { owner: repo.owner, repo: repo.repo, ref }, { priority: 'interactive', signal });
  const o = r.repository?.object;
  return o?.oid ?? o?.target?.oid ?? null;
}

/** The listings open, the latest used last: a few commits' (each RepoTree keeps its files in memory; a complete one is also kept in IndexedDB). */
const trees = new Map<string, RepoTree>();
const TREES_OPEN = 4;

/**
 * The paths at `commit`, from the explorer's own listing (RepoTree: one
 * recursive call, kept in this browser when complete). When GitHub cut
 * the listing short, the folders on the way to `prefix` are read one
 * level at a time, and the listing says it is partial.
 */
export async function listTree(repo: Repo, commit: string, prefix: string): Promise<RefTree> {
  const key = `${repo.owner}/${repo.repo}@${commit}`.toLowerCase();
  let tree = trees.get(key);
  if (tree) trees.delete(key);
  else tree = new RepoTree(repo.owner, repo.repo, commit);
  trees.set(key, tree);
  while (trees.size > TREES_OPEN) trees.delete(trees.keys().next().value!);
  await tree.load();
  if (tree.status === 'error') throw new Error(tree.failure?.message ?? 'The listing could not be read');
  if (!tree.lazy) return { sha: commit, paths: tree.paths, truncated: false };
  // The folders on the way to what is typed, and the folder it ends in.
  await tree.reveal(prefix + (prefix.endsWith('/') || !prefix ? '' : '/'));
  return { sha: commit, paths: [...tree.paths, ...tree.dirPaths()].sort(), truncated: true };
}

/** The dashboard as last read; null when it was never read, or cannot be. */
async function savedDashboard() {
  if (typeof location === 'undefined') return null;
  try {
    return await dashboardStorage(location.origin).read();
  } catch {
    return null;
  }
}

/** The pull requests waiting on `me`, from the dashboard as last read. */
export async function readWaiting(me: string): Promise<WaitingPull[]> {
  return waitingOf(await savedDashboard(), me);
}

/** Every pull request on the dashboard as last read. */
export async function readDashboard(): Promise<WaitingPull[]> {
  return dashboardOf(await savedDashboard());
}

/**
 * The order of the jump bar's sources, by id: what the text names; paths
 * after `//`; in a repository, its branches and open pull requests; the
 * repository index's repositories, then the repository a text `o/r`
 * names (the index's richer row first, when it has one); the pull
 * requests waiting on the viewer, then the dashboard's others; recents
 * and pins.
 */
export const SOURCE_ORDER = ['direct', 'paths', 'branches', 'pulls', 'repositories', 'repository', 'waiting', 'dashboard', 'recents', 'pins'] as const;
export type SourceId = (typeof SOURCE_ORDER)[number];

/** Sources plugged in from outside the bar, by id (the repository index's `repositories`). */
const plugged = new Map<SourceId, Source>();

/**
 * Plug `source` in at its id's place in the order, in place of any the
 * app has there; the bar uses it from its next opening. The function
 * returned unplugs it.
 */
export function plugSource(id: SourceId, source: Source): () => void {
  plugged.set(id, source);
  return () => {
    if (plugged.get(id) === source) plugged.delete(id);
  };
}

/** `sources` in the order of their ids. */
export function ordered(sources: Partial<Record<SourceId, Source>>): Source[] {
  return SOURCE_ORDER.flatMap((id) => (sources[id] ? [sources[id]!] : []));
}

export interface AppSourcesOptions {
  /** Where URLs are read from: the app's origin and GitHub's. */
  context: () => Omit<InterpretContext, 'repo'>;
  me: () => string;
  recents: () => readonly Place[];
  pins: () => readonly Place[];
}

/** The app's sources, with whatever is plugged in, in `SOURCE_ORDER`. */
export function appSources(o: AppSourcesOptions): Source[] {
  return ordered({
    direct: directSource(o.context),
    paths: pathSource({ commit: readCommit, list: listTree }),
    branches: branchSource(readBranches),
    pulls: pullSource(readOpenPulls),
    repository: repositorySource(readRepository),
    waiting: waitingSource(() => readWaiting(o.me())),
    dashboard: dashboardSource(readDashboard),
    // A pinned subject is listed with the pins.
    recents: recentSource(() => o.recents().filter((r) => !o.pins().some((p) => p.key === r.key))),
    pins: pinSource(o.pins),
    ...Object.fromEntries(plugged),
  });
}
